import { useRef, useState } from "react";
import { TextInput } from "@sprout/text_input.tsx";
import { PhoneInput, type PhoneError } from "@sprout/phone_input.tsx";
import { LocationInput, type Location } from "@components/ui/location_input.tsx";
import useTranslation from "@hooks/use_translation.ts";
import ModalFooter from "./modal_footer.tsx";
import useSectionSave from "./use_section_save.ts";
import type { SectionProps } from "./types.ts";

const PHONE_ERROR_MESSAGES: Record<Exclude<PhoneError, "missing_country" | "unknown">, string> = {
  invalid: "is invalid",
  too_short: "is too short",
  too_long: "is too long",
};

const ContactInfoSection = ({ data, onClose, source }: SectionProps) => {
  const { t } = useTranslation("user");
  const { save, saving, errors } = useSectionSave();

  const [firstName, setFirstName] = useState(data.firstName || "");
  const [lastName, setLastName] = useState(data.lastName || "");
  const [preferredName, setPreferredName] = useState(data.preferredName || "");
  const [phoneNumber, setPhoneNumber] = useState(data.phoneNumber || "");
  const [phoneRealtimeError, setPhoneRealtimeError] = useState<PhoneError | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [countryError, setCountryError] = useState<string | null>(null);
  const [location, setLocation] = useState<Location | null>(data.location);
  const countryRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    let newPhoneError: string | null = null;
    let newCountryError: string | null = null;
    if (phoneNumber.length > 0 && phoneRealtimeError !== null) {
      if (phoneRealtimeError === "missing_country") {
        newCountryError = "Select a country";
      } else if (phoneRealtimeError !== "unknown") {
        newPhoneError = PHONE_ERROR_MESSAGES[phoneRealtimeError];
      }
    }
    setCountryError(newCountryError);
    setPhoneError(newPhoneError);
    if (newPhoneError || newCountryError) return false;

    const formData = new FormData();
    formData.append("user[first_name]", firstName);
    formData.append("user[last_name]", lastName);
    formData.append("personal_info[preferred_name]", preferredName);
    formData.append("personal_info[phone_number]", phoneNumber);
    if (location) {
      formData.append("location[display_name]", location.display_name);
      formData.append("location[latitude]", location.latitude.toString());
      formData.append("location[longitude]", location.longitude.toString());
      if (location.country_short_name) {
        formData.append("location[country_short_name]", location.country_short_name);
      }
    } else {
      formData.append("clear_location", "true");
    }

    const success = await save(formData);
    if (success) onClose();
    return success;
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <TextInput
          id="contact-first-name"
          label={t("fields.firstName")}
          value={firstName}
          onChange={(e) => setFirstName(e.currentTarget.value)}
          error={errors.first_name?.[0]}
        />
        <TextInput
          id="contact-last-name"
          label={t("fields.lastName")}
          value={lastName}
          onChange={(e) => setLastName(e.currentTarget.value)}
          error={errors.last_name?.[0]}
        />
        <TextInput
          id="contact-preferred-name"
          label={t("fields.preferredName")}
          optionalText={t("fields.optional")}
          value={preferredName}
          onChange={(e) => setPreferredName(e.currentTarget.value)}
          error={errors.preferred_name?.[0]}
        />
        <PhoneInput
          id="contact-phone"
          countryLabel={t("fields.country")}
          phoneLabel={t("fields.phone")}
          initialValue={data.phoneNumber || ""}
          onChange={setPhoneNumber}
          onError={setPhoneRealtimeError}
          countryError={countryError}
          phoneError={phoneError ? `Phone number ${phoneError}` : errors.phone_number?.[0] ?? null}
          countryRef={countryRef}
          phoneRef={phoneRef}
        />
        <LocationInput value={location} onChange={setLocation} />
      </div>
      <ModalFooter
        section="contact_info"
        onSave={handleSave}
        onCancel={onClose}
        saving={saving}
        source={source}
      />
    </>
  );
};

export default ContactInfoSection;
